/**
 * Helper utilities for working with document images from imgproxy
 *
 * These utilities help you find and work with page-specific thumbnails
 * from the thumbnails array that now contains ALL pages.
 */

import type { ImageproxyImagePreset } from '@/types/generated'

export interface PageThumbnail {
  pageNumber: number
  thumbnailUrl?: string
  smallUrl?: string
  mediumUrl?: string
  largeUrl?: string
}

/**
 * Get thumbnail URL for a specific page and preset from a thumbnails array
 *
 * @param thumbnails - The thumbnails array
 * @param pageNumber - Page number (1-based)
 * @param preset - Image preset ('thumbnail', 'small', 'medium', 'large')
 * @returns The thumbnail URL or null if not found
 */
export function getPageThumbnailUrl(
  thumbnails: ImageproxyImagePreset[],
  pageNumber: number,
  preset: string = 'thumbnail'
): string | null {
  if (!thumbnails || thumbnails.length === 0) return null

  // Group images by preset to understand the structure
  const imagesByPreset = thumbnails.reduce(
    (groups, img) => {
      if (img.image_preset) {
        if (!groups[img.image_preset]) {
          groups[img.image_preset] = []
        }
        groups[img.image_preset].push(img)
      }
      return groups
    },
    {} as Record<string, ImageproxyImagePreset[]>
  )

  // Get the images for the requested preset
  const presetImages = imagesByPreset[preset]
  if (!presetImages || presetImages.length === 0) return null

  // Convert 1-based page number to 0-based array index
  const imageIndex = pageNumber - 1

  // Check if the index is valid for this preset
  if (imageIndex < 0 || imageIndex >= presetImages.length) return null

  const pageImage = presetImages[imageIndex]

  return pageImage?.url || null
}

/**
 * Get all thumbnail URLs for a specific page from thumbnails array
 *
 * @param thumbnails - The thumbnails array
 * @param pageNumber - Page number (1-based)
 * @returns Object with all preset URLs for the page
 */
export function getPageThumbnails(
  thumbnails: ImageproxyImagePreset[],
  pageNumber: number
): PageThumbnail {
  return {
    pageNumber,
    thumbnailUrl: getPageThumbnailUrl(thumbnails, pageNumber, 'thumbnail') || undefined,
    smallUrl: getPageThumbnailUrl(thumbnails, pageNumber, 'small') || undefined,
    mediumUrl: getPageThumbnailUrl(thumbnails, pageNumber, 'medium') || undefined,
    largeUrl: getPageThumbnailUrl(thumbnails, pageNumber, 'large') || undefined
  }
}

/**
 * Get thumbnails for a range of pages from thumbnails array
 *
 * @param thumbnails - The thumbnails array
 * @param startPage - Start page number (1-based)
 * @param endPage - End page number (1-based)
 * @returns Array of page thumbnails
 */
export function getPageRangeThumbnails(
  thumbnails: ImageproxyImagePreset[],
  startPage: number,
  endPage: number
): PageThumbnail[] {
  const pageThumbnails: PageThumbnail[] = []

  for (let page = startPage; page <= endPage; page++) {
    pageThumbnails.push(getPageThumbnails(thumbnails, page))
  }

  return pageThumbnails
}

/**
 * Get all available page numbers that have thumbnails from thumbnails array
 *
 * @param thumbnails - The thumbnails array
 * @returns Array of page numbers that have thumbnails
 */
export function getAvailablePageNumbers(thumbnails: ImageproxyImagePreset[]): number[] {
  if (!thumbnails) return []

  // Since pages are represented by array indices, we can determine
  // available pages based on the array length and group by presets
  const presetGroups = new Map<string, number>()

  thumbnails.forEach((img) => {
    if (img.image_preset) {
      const current = presetGroups.get(img.image_preset) || 0
      presetGroups.set(img.image_preset, current + 1)
    }
  })

  // Assume all presets have the same number of pages
  const maxPages = Math.max(...Array.from(presetGroups.values()), 0)

  // Generate page numbers from 1 to maxPages
  return Array.from({ length: maxPages }, (_, i) => i + 1)
}

/**
 * Check if thumbnails are available for a specific page from thumbnails array
 *
 * @param thumbnails - The thumbnails array
 * @param pageNumber - Page number to check
 * @returns True if thumbnails are available for this page
 */
export function hasPageThumbnails(
  thumbnails: ImageproxyImagePreset[],
  pageNumber: number
): boolean {
  if (!thumbnails) return false
  const availablePages = getAvailablePageNumbers(thumbnails)
  return availablePages.includes(pageNumber)
}

/**
 * Get the first page thumbnail from thumbnails array (backwards compatibility)
 * This maintains compatibility with existing code that expects first page thumbnail
 *
 * @param thumbnails - The thumbnails array
 * @param preset - Image preset
 * @returns First page thumbnail URL
 */
export function getFirstPageThumbnail(
  thumbnails: ImageproxyImagePreset[],
  preset: string = 'thumbnail'
): string | null {
  return getPageThumbnailUrl(thumbnails, 1, preset)
}

/**
 * Example usage in components:
 *
 * Note: Pages are now referenced by array index, where page 1 = index 0, page 2 = index 1, etc.
 * The functions handle this conversion automatically.
 *
 * ```typescript
 * // Get thumbnail for page 5 from thumbnails array
 * const page5Thumbnail = getPageThumbnailUrl(thumbnails, 5, 'thumbnail')
 *
 * // Get all thumbnails for page 10 from thumbnails array
 * const page10Thumbs = getPageThumbnails(thumbnails, 10)
 *
 * // Get thumbnails for pages 1-10 from thumbnails array
 * const firstTenPages = getPageRangeThumbnails(thumbnails, 1, 10)
 *
 * // Check what pages have thumbnails in thumbnails array
 * const availablePages = getAvailablePageNumbers(thumbnails)
 * ```
 */
